/**
 * Phase 1 keyword-picker — bulk-track mutation. Backs the "Track selected"
 * dialog on the keyword-run detail page.
 *
 * The API returns partial success (created[] + skipped[]) so the caller can
 * render both, e.g. "created 4, skipped 1 (already tracked)".
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  CreateTrackedKeywordsBulkRequest,
  CreateTrackedKeywordsBulkResponse,
} from '@seo/types';
import { apiPost } from '@/lib/api';

export function useCreateTrackedKeywordsBulk() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateTrackedKeywordsBulkRequest) =>
      apiPost<CreateTrackedKeywordsBulkResponse>('/v1/tracked-keywords/bulk', input, {
        headers: { 'idempotency-key': crypto.randomUUID() },
      }),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['tracked-keywords', vars.project_id] });
      void qc.invalidateQueries({ queryKey: ['rank-summary', vars.project_id] });
      void qc.invalidateQueries({ queryKey: ['impact-cards', vars.project_id] });
    },
  });
}
